import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { LoggerService } from '@fry/lib/utils';
import { AuthService } from '@fry/lib/auth/auth.service';
import { OidcService } from '../core';
import { ClusterService } from '../core/cluster.service';


interface RequestOptions {
  headers?: HttpHeaders;
  params?: HttpParams;
  data?: any,
  responseType?: 'json';
}


@Injectable({
  providedIn: 'root'
})
export class APIService {

  constructor(
    private http: HttpClient,
    private logger: LoggerService,
    private cluster: ClusterService,
    private auth: AuthService,
    private oidc: OidcService,

    @Inject(LOCALE_ID) private locale: string,
  ) {  }

  private errorHandler = (response: HttpErrorResponse) => {
    if (response.error.message) {
      this.logger.error('API: Known error', response.error.message);
      return throwError(() => response.error.message);
    }

    this.logger.error('API: An unknown error occurred', response);
    if (response.statusText) {
      return throwError(() => response.statusText);
    }

    return throwError(() => 'API: Unknown error occurred');
  }

  public get apiUrl() {
    const org = this.cluster.getCurrentOrganisation();
    return org?.urls.api;
  }

  private getUrl(endpoint: string) {
    return new URL(endpoint, this.apiUrl).href;
  }

  private getHeaders(headers?: any): HttpHeaders {
    let authHeaders = {};
    const apiInfo = this.oidc.apiInfo();
    if (this.auth.isLoggedIn) {
      authHeaders = {
        'Authorization': `${apiInfo.token}`,
        'EAS-Organisation': `${apiInfo.organisation}`,
        'Accept-Language': this.locale
      };
    } else {
      authHeaders = {
        'Accept-Language': this.locale,
        'EAS-Organisation': `${apiInfo.organisation}`,
      };
    }

    const httpHeaders = {
      ...authHeaders,
      ...(headers || {}),
    };

    return new HttpHeaders(httpHeaders);
  }

  private call(method: string, endpoint: string, options: RequestOptions, retry: number=0): Observable<any> {
    const httpOptions  = {
      headers: options.headers || this.getHeaders(),
      params: options.params,
      responseType: options.responseType || 'json',
      body: options.data
    };

    let url;
    try {
      url = this.getUrl(endpoint);
    } catch (e) {
      this.logger.error('API: Invalid endpoint', endpoint);
      if (retry < 2) {
        return from(this.oidc.renewToken()).pipe(
          switchMap(() => this.call(method, endpoint, options, retry + 1))
        );
      } else {
        return throwError(() => 'API: Invalid endpoint');
      }
    }

    return this.http.request<any>(method, url, {
      ...httpOptions
    }).pipe(
      catchError(err => {
        if (err.status === 401 && retry < 1) {
          if (err?.error?.message && err.error.message.includes('stepup')) {
            return from(this.oidc.stepUp()).pipe(
              switchMap(() => this.call(method, endpoint, options, retry + 1))
            );
          } else {
            return from(this.oidc.renewToken()).pipe(
              switchMap(() => this.call(method, endpoint, options, retry + 1))
            );
          }
        } else {
          return this.errorHandler(err);
        }
      })
    );
  }

  public get(endpoint: string, params?: any) {
    const httpParams = new HttpParams({ fromObject: params });
    return this.call('GET', endpoint, { params: httpParams });
  }

  public post(endpoint: string, data: any, headers?: HttpHeaders) {
    const httpOptions: RequestOptions = {
      headers: this.getHeaders(headers),
      responseType: 'json' as 'json',
      data: data
    };
    if (headers && headers.has('accept')) {
      httpOptions.responseType = 'blob' as 'json';
    }
    return this.call('POST', endpoint, httpOptions);
  }

  public put(endpoint: string, data: any) {
    return this.call('PUT', endpoint, { data: data });
  }

  public delete(endpoint: string) {
    return this.call('DELETE', endpoint, {});
  }

  public patch(endpoint: string, data: any) {
    return this.call('PATCH', endpoint, { data: data });
  }

  /* This is a hack to get around importing auth in stores */
  get organisation() {
    return this.auth.organisation;
  }
}
